import {Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {RodizioLojaService} from '../../services/rodizio-loja.service';
import {CarrinhoService} from '../../services/carrinho.service';
import {DominiosService} from '../../services/dominios.service';
import {EstadoRodizioLoja} from '../../objeto/EstadoRodizioLoja';
import {DialogService} from '@progress/kendo-angular-dialog';
import {ModalVincularComandaComponent} from '../../loja-modal-vincular-comanda/modal-vincular-comanda.component';

type FolhaRodizio = 'codigo' | 'planos' | 'rodada' | 'conta' | null;

const INTERVALO_ENTRADA_AUTOMATICA = 20000;

const MAXIMO_PESSOAS = 30;

/**
 * Rodízio no celular, na página da mesa: o cartão de começar (plano, preço por pessoa, pessoas e Começar, tudo no
 * cartão), o QR libera sozinho quando o garçom lançou (o código fica para mesa com mais de um rodízio), rodada (os
 * itens do carrinho vão direto para a cozinha) e conta com as rodadas e o saldo.
 */
@Component({
  selector: 'app-rodizio-mesa',
  templateUrl: './rodizio-mesa.component.html',
  styleUrls: ['./rodizio-mesa.component.scss']
})
export class RodizioMesaComponent implements OnInit, OnDestroy {
  @Input() mesa: any;
  @Input() empresa: any;
  @Input() produtosPorCategoria: any = {};
  /** Acesso pelo QR do cartão da comanda: ainda não há mesa; o celular lê a mesa na hora de começar. */
  @Input() tokenCartao: string;

  // folhas e aviso vão para o body (acima da barra de categorias, z 9999); o Angular remove de lá ao destruir
  private camadaNoBody: HTMLElement;
  @ViewChild('camada') set camada(elemento: ElementRef) {
    this.camadaNoBody = elemento ? elemento.nativeElement : null;
    if (this.camadaNoBody && this.camadaNoBody.parentNode !== document.body) document.body.appendChild(this.camadaNoBody);
  }

  folha: FolhaRodizio = null;
  digitos: string[] = ['', '', '', ''];
  erro: string = null;
  enviando = false;
  aviso: string = null;
  avisoDetalhe: string = null;
  /** Por que o QR da mesa ainda não liberou (o servidor explica; a faixa mostra). */
  motivoSemAcesso: string = null;
  pessoas = 1; // começa em 1: quem está sozinho não paga em dobro sem reparar; o atendente confere no fechamento
  /** Quem está começando: vai para a comanda como identificação leve (sem telefone); vem preenchido se a loja já o conhece. */
  nome = '';
  planoEscolhido: any = null;
  private timerAviso: any;
  private timerConta: any;
  private timerEntrada: any;
  private inscricoes: Subscription[] = [];

  constructor(public rodizioService: RodizioLojaService, private carrinhoService: CarrinhoService,
              private dominiosService: DominiosService, private activatedRoute: ActivatedRoute,
              private router: Router, private dialogService: DialogService) {}

  async ngOnInit() {
    this.inscricoes.push(this.activatedRoute.queryParams.subscribe((params: any) => {
      if (params && params.rodada && EstadoRodizioLoja.ativo) this.abraFolha('rodada');
    }));
    // entrou, saiu ou o código mudou: o carrinho volta a mostrar o mesmo preço que o servidor vai cobrar
    this.inscricoes.push(EstadoRodizioLoja.mudou.subscribe(() => {
      this.recalculeCarrinho();
      this.garantaPlanoEscolhido();
    }));

    this.nome = this.nomeConhecido();
    await this.rodizioService.carreguePlanos();
    this.garantaPlanoEscolhido();
    await this.rodizioService.atualize();
    await this.entreSozinho();
    // o cliente costuma ler o QR antes de o garçom lançar o plano: tenta de novo de tempos em tempos
    this.timerEntrada = setInterval(() => this.entreSozinho(), INTERVALO_ENTRADA_AUTOMATICA);

    if (this.rodizioService.rodadaEnviada && Date.now() - this.rodizioService.rodadaEnviada.horario < 8000)
      this.mostreAviso(`Rodada ${this.rodizioService.rodadaEnviada.codigo || ''} enviada`, 'A cozinha já recebeu. Acompanhe na conta.');
    if (this.activatedRoute.snapshot.queryParams.rodada && this.ativo) this.abraFolha('rodada');
  }

  ngOnDestroy() {
    this.inscricoes.forEach((inscricao) => inscricao.unsubscribe());
    clearTimeout(this.timerAviso);
    clearInterval(this.timerConta);
    clearInterval(this.timerEntrada);
    if (this.camadaNoBody && this.camadaNoBody.parentNode) this.camadaNoBody.parentNode.removeChild(this.camadaNoBody);
  }

  get ativo(): boolean {
    return EstadoRodizioLoja.ativo;
  }

  get rodizio(): any {
    return EstadoRodizioLoja.rodizio;
  }

  get planos(): any[] {
    return EstadoRodizioLoja.planos;
  }

  get exibir(): boolean {
    return (!!this.mesa || !!this.tokenCartao) && (this.ativo || this.planos.length > 0);
  }

  /** Entrou pelo cartão e ainda não leu a mesa: começar passa pela leitura da mesa (a mesma do carrinho). */
  get exigeMesa(): boolean {
    return !this.mesa && !!this.tokenCartao;
  }

  get itensDaRodada(): any[] {
    const pedido: any = this.carrinhoService.obtenhaPedido();
    return (pedido && pedido.itens) || [];
  }

  get qtdeItensRodada(): number {
    return this.itensDaRodada.reduce((soma: number, item: any) => soma + (Number(item.qtde) || 0), 0);
  }

  get itensIncluidos(): any[] {
    return this.itensDaRodada.filter((item: any) => this.incluido(item));
  }

  get itensAParte(): any[] {
    return this.itensDaRodada.filter((item: any) => !this.incluido(item));
  }

  /**
   * A mesma conta do servidor (máximo × pessoas, descontando o que a mesa já pediu) feita antes de enviar, para o
   * cliente ajustar a rodada em vez de ser recusado; quem decide continua sendo o servidor.
   */
  get excessoDeLimite(): string {
    const rodizio = this.rodizio;
    const pessoas = Number(rodizio && rodizio.plano && rodizio.plano.pessoas) || 0;
    for (const categoria of (rodizio && rodizio.categorias) || []) {
      const maximo = Number(categoria.maximoPorPessoa) || 0;
      if (!maximo || !pessoas) continue;

      const naRodada = this.itensIncluidos
        .filter((item: any) => EstadoRodizioLoja.categoriaDoProduto(item.produto) === Number(categoria.id))
        .reduce((soma: number, item: any) => soma + (Number(item.qtde) || 0), 0);
      const resta = Math.max(0, maximo * pessoas - (Number(categoria.jaPedidos) || 0));
      if (naRodada <= resta) continue;

      const nome = this.nomeDaSecao(rodizio, categoria);
      return resta
        ? `${nome} é ${maximo} por pessoa: esta mesa ainda pode pedir ${resta}. Tire ${naRodada - resta} para enviar.`
        : `${nome} é ${maximo} por pessoa e esta mesa já pediu todas. Tire da rodada para enviar.`;
    }

    // limite de peças a caminho: o que ainda está na cozinha mais as incluídas desta rodada
    const maximoPendente = Number(rodizio && rodizio.maximoPendente) || 0;
    const novas = this.itensIncluidos.reduce((soma: number, item: any) => soma + (Number(item.qtde) || 0), 0);
    if (maximoPendente && novas) {
      const pendentes = Number(rodizio.pecasPendentes) || 0;
      if (pendentes + novas > maximoPendente) {
        const resta = Math.max(0, maximoPendente - pendentes);
        return `Esta mesa tem ${pendentes} ${pendentes === 1 ? 'peça' : 'peças'} a caminho e o limite é ${maximoPendente} por vez. `
          + (resta ? `Dá para pedir ${resta} agora: tire ${novas - resta} para enviar.` : 'Espere chegar para pedir mais.');
      }
    }
    return null;
  }

  get aParteDaRodada(): number {
    return Number(this.itensDaRodada.reduce((soma: number, item: any) => soma + (Number(item.total) || 0), 0).toFixed(2));
  }

  incluido(item: any): boolean {
    return EstadoRodizioLoja.produtoIncluido(item && item.produto);
  }

  produtosDaCategoria(categoria: any): any[] {
    return ((this.produtosPorCategoria && this.produtosPorCategoria[categoria.nome]) || [])
      .filter((produto: any) => produto.tipo !== 'informativo');
  }

  abraFolha(folha: FolhaRodizio) {
    this.erro = null;
    this.folha = folha;
    clearInterval(this.timerConta);

    if (folha === 'codigo') {
      this.digitos = ['', '', '', ''];
      setTimeout(() => this.foqueDigito(0), 50);
    }
    if (folha === 'conta') {
      this.rodizioService.atualize();
      this.timerConta = setInterval(() => this.rodizioService.atualize(), 20000);
    }
    if (folha === 'rodada') {
      this.recalculeCarrinho();
      if (this.ativo) this.rodizioService.atualize(); // peças a caminho mudam conforme a cozinha anda
    }
  }

  /** O cartão já mostra o plano escolhido antes de qualquer folha: o primeiro, até o cliente trocar no chip. */
  private garantaPlanoEscolhido() {
    if (!this.planos.includes(this.planoEscolhido)) this.planoEscolhido = this.planos[0] || null;
  }

  fecheFolha() {
    this.folha = null;
    clearInterval(this.timerConta);
    if (this.activatedRoute.snapshot.queryParams.rodada)
      this.router.navigate([], {queryParams: {rodada: null}, queryParamsHandling: 'merge', replaceUrl: true});
  }

  digitou(indice: number, evento: any) {
    const valor = String(evento.target.value || '').replace(/\D/g, '').slice(-1);
    this.digitos[indice] = valor;
    evento.target.value = valor;
    this.erro = null;
    if (valor && indice < 3) this.foqueDigito(indice + 1);
    if (this.digitos.join('').length === 4) this.entre();
  }

  apagou(indice: number, evento: KeyboardEvent) {
    if (evento.key === 'Backspace' && !this.digitos[indice] && indice > 0) this.foqueDigito(indice - 1);
  }

  colou(evento: ClipboardEvent) {
    const texto = (evento.clipboardData && evento.clipboardData.getData('text')) || '';
    const numeros = texto.replace(/\D/g, '').slice(0, 4).split('');
    if (numeros.length !== 4) return;
    evento.preventDefault();
    this.digitos = numeros;
    this.entre();
  }

  async entre() {
    const codigo = this.digitos.join('');
    if (codigo.length !== 4 || this.enviando) return;

    this.enviando = true;
    this.erro = null;
    try {
      await this.rodizioService.entre(this.mesa.hash || this.dominiosService.hashMesa, codigo);
      this.recalculeCarrinho();
      this.fecheFolha();
      this.mostreAviso('Rodízio liberado neste celular');
    } catch (erro) {
      this.erro = erro || 'Não foi possível conferir o código.';
      this.digitos = ['', '', '', ''];
      setTimeout(() => this.foqueDigito(0), 50);
    } finally {
      this.enviando = false;
    }
  }

  mudePessoas(delta: number) {
    this.pessoas = Math.min(MAXIMO_PESSOAS, Math.max(1, this.pessoas + delta));
  }

  /** Tira o prefixo que todas as seções do plano repetem ("Festival · Sushi" vira "Sushi" na etiqueta). */
  nomeDaSecao(plano: any, categoria: any): string {
    const nomes: string[] = ((plano && plano.categorias) || []).map((c: any) => String(c.nome || ''));
    const prefixo = (nomes[0] || '').includes(' · ') ? nomes[0].split(' · ')[0] + ' · ' : null;
    const nome = String(categoria.nome || '');
    return prefixo && nomes.length > 1 && nomes.every((n) => n.startsWith(prefixo)) ? nome.slice(prefixo.length) : nome;
  }

  totalDoPlano(plano: any): number {
    return Number(((Number(plano && plano.precoPorPessoa) || 0) * this.pessoas).toFixed(2));
  }

  /** Loja com cartão cliente: o rodízio fica na comanda do cartão, então o celular lê o cartão antes de ativar. */
  get exigeCartao(): boolean {
    return !!(this.empresa && this.empresa.usarCartaoCliente && this.mesa && !this.mesa.naoGerarComanda);
  }

  /** A casa que aceita pedido de mesa sem identificação começa sem nome; nas outras o nome é obrigatório. */
  get exigeNome(): boolean {
    return !(this.empresa && this.empresa.pedidoMesaNaoIdentificado);
  }

  digitouNome(evento: any) {
    this.nome = String(evento.target.value || '');
    this.erro = null;
  }

  /** O cliente ativa o rodízio na própria mesa; o servidor lança o plano na comanda e libera este celular. */
  async ative(plano: any) {
    if (!plano || this.enviando) return;
    if (this.exigeNome && !this.nome.trim()) {
      this.erro = 'Diga seu nome para começar.';
      this.foqueNome();
      return;
    }
    if (this.exigeMesa) return this.leiaMesaEAtive(plano);
    if (this.exigeCartao) return this.leiaComandaEAtive(plano);

    await this.ativeNaComanda(plano, null);
  }

  /**
   * Acesso pelo cartão: lê o QR da mesa, grava mesa e comanda no pedido (o mesmo que o carrinho faz ao finalizar) e
   * lança o plano na comanda do cartão.
   */
  private leiaMesaEAtive(plano: any) {
    const pessoas = this.pessoas;
    this.fecheFolha();
    const dialogo: any = this.dialogService.open({title: '', content: ModalVincularComandaComponent, width: 400, height: 'auto'});
    const leitor: any = dialogo.content.instance;
    leitor.modo = 'MESA';
    leitor.tokenCartao = this.tokenCartao;
    dialogo.result.subscribe((comanda: any) => {
      if (!comanda || !comanda.id || !comanda.mesa) return;
      const pedido: any = this.carrinhoService.obtenhaPedido();
      pedido.mesa = comanda.mesa;
      pedido.mesa.hash = comanda.hashSessaoMesa || comanda.mesa.hash;
      pedido.mesa.codigoCartaoCliente = comanda.codigoCartaoCliente;
      this.dominiosService.hashMesa = pedido.mesa.hash;
      delete pedido.tokenCartaoPendente;
      delete pedido.codigoCartaoPendente;
      pedido.idComanda = comanda.id;
      pedido.idCartaoCliente = comanda.idCartaoCliente;
      pedido.comandaValidada = {idComanda: comanda.id, idCartaoCliente: comanda.idCartaoCliente,
        codigoPdvComanda: comanda.codigoPdvComanda, codigoCartaoCliente: comanda.codigoCartaoCliente};
      this.carrinhoService.salvePedido(pedido);
      this.mesa = pedido.mesa;
      this.tokenCartao = null;
      this.pessoas = pessoas;
      this.ativeNaComanda(plano, comanda.id);
    });
  }

  /** Mesma leitura de cartão do pedido de mesa; a folha fecha antes porque fica por cima do diálogo da loja. */
  private leiaComandaEAtive(plano: any) {
    const pessoas = this.pessoas;
    this.fecheFolha();
    const dialogo: any = this.dialogService.open({title: '', content: ModalVincularComandaComponent, width: 400, height: 'auto'});
    const leitor: any = dialogo.content.instance;
    leitor.mesa = this.mesa;
    leitor.modo = 'CARTAO';
    dialogo.result.subscribe((comanda: any) => {
      if (!comanda || !comanda.id) return;
      this.pessoas = pessoas;
      this.ativeNaComanda(plano, comanda.id);
    });
  }

  private async ativeNaComanda(plano: any, idComanda: number) {
    this.enviando = true;
    this.erro = null;
    try {
      await this.rodizioService.ative(this.mesa.hash || this.dominiosService.hashMesa, plano.id, this.pessoas, idComanda,
        this.nome.trim() || null);
      this.motivoSemAcesso = null;
      this.recalculeCarrinho();
      this.fecheFolha();
      this.mostreAviso(`${plano.nome} começou nesta mesa`);
    } catch (erro) {
      // o motivo aparece no cartão (a folha, se estava aberta, fechou para a leitura do cartão)
      this.fecheFolha();
      this.planoEscolhido = plano;
      this.erro = erro || 'Não foi possível começar o rodízio. Chame o atendente.';
    } finally {
      this.enviando = false;
    }
  }

  /** QR da mesa sem código: só enquanto nunca entrou (código trocado ou comanda fechada exigem o atendente). */
  async entreSozinho() {
    if (this.ativo || this.enviando || this.folha === 'codigo' || !this.mesa || !this.planos.length) return;
    const situacao = this.rodizioService.situacao;
    if ((situacao !== 'SEM_ACESSO' && situacao !== 'SEM_PLANO') || (typeof document !== 'undefined' && document.hidden)) return;

    try {
      await this.rodizioService.entre(this.mesa.hash || this.dominiosService.hashMesa);
      this.motivoSemAcesso = null;
      if (!this.ativo) return; // SEM_PLANO: o cartão de começar fica, sem mensagem
      this.recalculeCarrinho();
      if (this.folha === 'planos') this.fecheFolha();
      this.mostreAviso('Rodízio liberado neste celular');
    } catch (erro) {
      this.motivoSemAcesso = typeof erro === 'string' ? erro : null;
    }
  }

  /** − no último tira o item da rodada. */
  mudeQtde(item: any, delta: number) {
    const qtde = (Number(item.qtde) || 0) + delta;
    if (qtde < 1) return this.removaItem(item);

    const pedido: any = this.carrinhoService.obtenhaPedido();
    item.qtde = qtde;
    if (item.atualizeTotal) item.atualizeTotal();
    if (pedido.calculeTotal) pedido.calculeTotal();
    this.carrinhoService.salvePedido(pedido);
    this.rodizioService.descarteChavePendente();
  }

  removaItem(item: any) {
    const pedido: any = this.carrinhoService.obtenhaPedido();
    pedido.removaItem(item);
    this.carrinhoService.salvePedido(pedido);
    this.rodizioService.descarteChavePendente();
  }

  async envieRodada() {
    const pedido: any = this.carrinhoService.obtenhaPedido();
    if (!pedido || !pedido.itens || !pedido.itens.length || this.enviando) return;

    this.enviando = true;
    this.erro = null;
    try {
      const enviados = pedido.itens.slice();
      const resposta = await this.rodizioService.envieRodada(enviados);
      for (const item of enviados) pedido.removaItem(item);
      this.carrinhoService.salvePedido(pedido);
      this.fecheFolha();
      this.mostreAviso(`Rodada ${resposta.pedido && resposta.pedido.codigo ? resposta.pedido.codigo + ' ' : ''}enviada`,
        'A cozinha já recebeu. Acompanhe na conta.');
    } catch (erro) {
      this.erro = erro || 'Não foi possível enviar a rodada. Tente de novo.';
      await this.rodizioService.atualize();
    } finally {
      this.enviando = false;
    }
  }

  async pecaParaEncerrar() {
    if (this.enviando || (this.rodizio && this.rodizio.encerramentoSolicitado)) return;
    this.enviando = true;
    this.erro = null;
    try {
      await this.rodizioService.soliciteEncerramento();
    } catch (erro) {
      this.erro = erro || 'Não foi possível avisar o atendente.';
    } finally {
      this.enviando = false;
    }
  }

  /** "2 Niguiri de Salmão, 1 Heineken" — o que foi na rodada, não só a quantidade de itens. */
  resumoDaRodada(rodada: any): string {
    const itens = (rodada && rodada.itens) || [];
    if (!itens.length) return `${rodada.qtdeItens || 0} ${rodada.qtdeItens === 1 ? 'item' : 'itens'}`;
    return itens.map((item: any) => `${item.qtde} ${item.nome}`).join(', ');
  }

  textoSituacao(): string {
    const situacao = this.rodizioService.situacao;
    if (situacao === 'CODIGO_TROCADO' || situacao === 'ENCERRADO')
      return this.rodizioService.mensagem;
    return null;
  }

  private recalculeCarrinho() {
    const pedido: any = this.carrinhoService.obtenhaPedido();
    if (!pedido || !pedido.itens) return;
    pedido.itens.forEach((item: any) => item.atualizeTotal && item.atualizeTotal());
    if (pedido.calculeTotal) pedido.calculeTotal();
    this.carrinhoService.salvePedido(pedido);
  }

  private foqueDigito(indice: number) {
    const campo: any = document.getElementById('rodizio-digito-' + indice);
    if (campo) campo.focus();
  }

  /** Nome que a loja já tem deste celular (pedido anterior), menos o marcador de não identificado. */
  private nomeConhecido(): string {
    const pedido: any = this.carrinhoService.obtenhaPedido();
    const nome = String((pedido && pedido.contato && pedido.contato.nome) || '').trim();
    return nome === 'Consumidor não identificado' ? '' : nome;
  }

  private foqueNome() {
    // o campo existe no cartão e no pé da folha; o visível é o que recebe o foco
    const campos: any[] = Array.from(document.querySelectorAll('.rodizio-nome__campo'));
    const campo = campos.find((c: any) => c.offsetParent !== null) || campos[0];
    if (campo) campo.focus();
  }

  private mostreAviso(texto: string, detalhe: string = null) {
    this.aviso = texto;
    this.avisoDetalhe = detalhe;
    clearTimeout(this.timerAviso);
    this.timerAviso = setTimeout(() => { this.aviso = null; this.avisoDetalhe = null; }, 4000);
  }
}
